import { IS_NATIVE, IS_MOBILE, PLATFORM } from "../config.js";
import { isDismissal } from "./files.js";
import { loadShare } from "./plugins.js";

/** Whether shareOrCopy() will open a share sheet rather than copy. */
export function hasShareSheet() {
  return IS_NATIVE || (IS_MOBILE && typeof navigator !== "undefined" && !!navigator.share);
}

/**
 * Sharing a layout link. Native and mobile browsers get the system share sheet; desktop
 * copies to the clipboard even where navigator.share exists, since a desktop share sheet
 * is a detour from where a link is usually pasted. The copy has an execCommand fallback
 * for insecure contexts and older Safari, where navigator.clipboard is absent.
 *
 * @returns {Promise<'shared'|'copied'|'failed'>} — the caller picks the toast, since a
 *   share sheet is its own feedback and a copy is not.
 */
export async function shareOrCopy({ url, title, text, dialogTitle } = {}) {
  if (IS_NATIVE) {
    try {
      const { Share } = await loadShare();
      // A good number of Android targets drop `url`, so `text` carries it there too. iOS
      // shares each field as its own item, so the same fallback hands it two links.
      const body = PLATFORM === "android" ? text ?? url : text;
      await Share.share({ title, text: body, url, dialogTitle: dialogTitle ?? title });
      return "shared";
    } catch (error) {
      if (isDismissal(error)) return "shared";
      console.error("[platform] share failed:", error);
      // Fall through to the clipboard rather than leaving the user with nothing.
    }
  } else if (hasShareSheet()) {
    try {
      await navigator.share({ title, text, url });
      return "shared";
    } catch (error) {
      if (isDismissal(error)) return "shared";
      // NotAllowedError when the tap's user activation was spent; copy instead.
    }
  }

  return copyText(url ?? text ?? "");
}

/** @returns {Promise<'copied'|'failed'>} */
export async function copyText(value) {
  try {
    await navigator.clipboard.writeText(value);
    return "copied";
  } catch {
    return copyViaExecCommand(value) ? "copied" : "failed";
  }
}

// navigator.clipboard is undefined outside a secure context; this is the only way to
// copy there. Deprecated, but still the universal fallback.
function copyViaExecCommand(value) {
  try {
    const area = document.createElement("textarea");
    area.value = value;
    area.setAttribute("readonly", "");
    area.style.cssText = "position:fixed;top:-9999px;opacity:0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    area.remove();
    return ok;
  } catch {
    return false;
  }
}
